import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  memo,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from "react";
import { Link } from "wouter";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { UserButton, useUser } from "@clerk/clerk-react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { toast } from "sonner";
import {
  Wallet,
  Phone,
  Lock,
  Loader2,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Bell,
  MessageSquare,
  CheckCircle2,
  AlertCircle,
  Clock,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Paywall from "@/components/Paywall";
import { SUBSCRIPTION_PRICE_IDS, type SubscriptionPlan } from "@/lib/subscriptions";
import { PromotionSpotlight } from "@/components/PromotionSpotlight";
import { Navbar } from "@/components/Navbar";

export default function Profile() {
  const { t } = useTranslation();
  const { user: clerkUser } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    bio: "",
  });
  const [walletData, setWalletData] = useState({
    walletAddress: "",
    signature: "",
  });
  const [showWalletForm, setShowWalletForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Convex queries and mutations
  const profile = useQuery(api.auth.getCurrentUser);
  const userAds = useQuery(api.ads.getUserAds);
  const responses = useQuery(api.interactions.getUserResponses);
  const notifications = useQuery(api.interactions.getUserNotifications);
  const updateProfileMutation = useMutation(api.auth.updateProfile);
  const verifyWalletMutation = useMutation(api.auth.verifyWallet);
  const createCheckoutSession = useAction(api.stripe.createCheckoutSession);

  useEffect(() => {
    if (profile && !isEditing) {
      setFormData({
        name: profile.name ?? "",
        email: profile.email ?? "",
        phone: profile.phone ?? "",
        bio: profile.bio ?? "",
      });
    }
  }, [profile, isEditing]);

  const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);

  // Stable callback for input changes
  const handleInputChange = useCallback((
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Stable callback for wallet input changes
  const handleWalletInputChange = useCallback((
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setWalletData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Stable callback for edit toggle
  const handleStartEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancelEditing = useCallback(() => {
    setIsEditing(false);
    if (profile) {
      setFormData({
        name: profile.name ?? "",
        email: profile.email ?? "",
        phone: profile.phone ?? "",
        bio: profile.bio ?? "",
      });
    }
  }, [profile]);

  // Stable callback for wallet form toggle
  const handleShowWalletForm = useCallback(() => {
    setShowWalletForm(true);
  }, []);

  const handleHideWalletForm = useCallback(() => {
    setShowWalletForm(false);
    setWalletData({ walletAddress: "", signature: "" });
  }, []);

  // Stable callback for profile update
  const handleUpdateProfile = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await updateProfileMutation({
        name: formData.name || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        bio: formData.bio || undefined,
      });
      toast.success("Profile updated successfully");
      setIsEditing(false);
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, updateProfileMutation]);

  // Stable callback for wallet verification
  const handleVerifyWallet = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!walletData.walletAddress) {
      toast.error("Please enter a wallet address");
      return;
    }

    setIsSubmitting(true);
    try {
      await verifyWalletMutation({
        walletAddress: walletData.walletAddress,
        signature: walletData.signature || undefined,
      });
      toast.success("Wallet verified successfully!");
      setShowWalletForm(false);
      setWalletData({ walletAddress: "", signature: "" });
    } catch (error: any) {
      toast.error(error.message || "Failed to verify wallet");
    } finally {
      setIsSubmitting(false);
    }
  }, [walletData, verifyWalletMutation]);

  const isLoading =
    profile === undefined ||
    userAds === undefined ||
    responses === undefined ||
    notifications === undefined;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />

        <div className="container max-w-3xl py-24">
          <Card className="border-dashed">
            <CardHeader>
              <CardTitle className="text-2xl">Let us build your profile</CardTitle>
              <CardDescription>
                We could not load your profile record yet. Please try again in a moment or refresh to sync with Clerk.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  const allAds = useMemo(() => (userAds ?? []) as any[], [userAds]);
  const responseItems = useMemo(() => (responses ?? []) as any[], [responses]);
  const notificationItems = useMemo(() => (notifications ?? []) as any[], [notifications]);

  const displayName = profile.name || clerkUser?.fullName || "Marketplace Explorer";
  const primaryEmail =
    profile.email || clerkUser?.primaryEmailAddress?.emailAddress || "Email pending";
  const avatarImage = profile.profileImage || clerkUser?.imageUrl || undefined;

  const initials = useMemo(() => {
    return displayName
      .split(" ")
      .map((part: string) => part.charAt(0))
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }, [displayName]);

  // Memoize computed stats
  const { totalAds, activeAds, totalViews, newResponsesCount, unreadNotifications } = useMemo(() => ({
    totalAds: allAds.length,
    activeAds: allAds.filter((ad) => ad.status === "active").length,
    totalViews: allAds.reduce((acc, ad) => acc + (ad.views ?? 0), 0),
    newResponsesCount: responseItems.filter((r) => r.status === "new").length,
    unreadNotifications: notificationItems.filter((n) => !n.isRead).length,
  }), [allAds, responseItems, notificationItems]);

  const heroStats = useMemo(() => [
    {
      label: "Active listings",
      value: numberFormatter.format(activeAds),
      helper: `${numberFormatter.format(totalAds)} total`,
      icon: <TrendingUp className="size-4" />,
    },
    {
      label: "Audience views",
      value: numberFormatter.format(totalViews),
      helper: "Lifetime reach",
      icon: <Sparkles className="size-4" />,
    },
    {
      label: "New replies",
      value: numberFormatter.format(newResponsesCount),
      helper: `${numberFormatter.format(unreadNotifications)} unread notices`,
      icon: <MessageSquare className="size-4" />,
    },
  ], [numberFormatter, activeAds, totalAds, totalViews, newResponsesCount, unreadNotifications]);

  const completionItems = useMemo(() => [
    { label: "Upload a profile photo", completed: Boolean(avatarImage) },
    { label: "Add a short bio", completed: Boolean(profile.bio) },
    { label: "Verify your wallet", completed: Boolean(profile.isWalletVerified) },
    { label: "Share a contact number", completed: Boolean(profile.phone) },
    { label: "Accept safety pledge", completed: Boolean(profile.safetyTermsAcceptedAt) },
  ], [avatarImage, profile.bio, profile.isWalletVerified, profile.phone, profile.safetyTermsAcceptedAt]);

  const { completedSteps, completionValue } = useMemo(() => {
    const completed = completionItems.filter((item) => item.completed).length;
    return {
      completedSteps: completed,
      completionValue: completionItems.length
        ? Math.round((completed / completionItems.length) * 100)
        : 0,
    };
  }, [completionItems]);

  const lastSignedIn = useMemo(() => 
    profile.lastSignedIn ? new Date(profile.lastSignedIn) : null,
    [profile.lastSignedIn]
  );
  const memberSince = useMemo(() => 
    profile.createdAt ? new Date(profile.createdAt) : null,
    [profile.createdAt]
  );

  const heroBadges: ReactNode[] = useMemo(() => {
    const badges: ReactNode[] = [];
    badges.push(
      <Badge key="role" variant={profile.role === "admin" ? "destructive" : "secondary"}>
        {profile.role || "member"}
      </Badge>
    );

    if (profile.isWalletVerified) {
      badges.push(
        <Badge key="wallet" className="bg-emerald-500/10 text-emerald-400">
          <ShieldCheck className="size-3" /> Wallet verified
        </Badge>
      );
    }

    if (profile.safetyTermsAcceptedAt) {
      badges.push(
        <Badge key="safety" className="bg-sky-500/10 text-sky-400">
          <CheckCircle2 className="size-3" /> Safety pledge
        </Badge>
      );
    }

    return badges;
  }, [profile.role, profile.isWalletVerified, profile.safetyTermsAcceptedAt]);

  const latestAds = useMemo(() => allAds.slice(0, 3), [allAds]);
  const latestResponses = useMemo(() => responseItems.slice(0, 3), [responseItems]);
  const latestNotifications = useMemo(() => notificationItems.slice(0, 3), [notificationItems]);

  const trustSignals = useMemo(() => [
    {
      label: "Account standing",
      value: profile.isSuspended ? "Suspended" : "In good standing",
      tone: profile.isSuspended ? "danger" : "positive",
      helper: profile.isSuspended ? "Contact support to appeal" : "No strikes on record",
      icon: profile.isSuspended ? (
        <AlertCircle className="size-4" />
      ) : (
        <ShieldCheck className="size-4" />
      ),
    },
    {
      label: "Safety pledge",
      value: profile.safetyTermsAcceptedAt
        ? new Date(profile.safetyTermsAcceptedAt).toLocaleDateString()
        : "Pending",
      tone: profile.safetyTermsAcceptedAt ? "positive" : "warning",
      helper: profile.safetyTermsAcceptedAt
        ? "Thank you for keeping users safe"
        : "Accept the pledge when posting personal connections",
      icon: profile.safetyTermsAcceptedAt ? (
        <CheckCircle2 className="size-4" />
      ) : (
        <Lock className="size-4" />
      ),
    },
    {
      label: "Wallet verification",
      value: profile.isWalletVerified ? "Verified" : "Not verified",
      tone: profile.isWalletVerified ? "positive" : "neutral",
      helper: profile.isWalletVerified
        ? "Token-gated drops unlocked"
        : "Verify to access premium inventory",
      icon: <Wallet className="size-4" />,
    },
  ], [profile.isSuspended, profile.safetyTermsAcceptedAt, profile.isWalletVerified]);

  const toneStyles: Record<string, string> = {
    positive: "bg-emerald-500/10 text-emerald-500",
    warning: "bg-amber-500/10 text-amber-500",
    danger: "bg-rose-500/10 text-rose-500",
    neutral: "bg-muted text-muted-foreground",
  };

  const trustPreview = (
    <div className="grid gap-3 sm:grid-cols-2">
      {trustSignals.map((signal) => (
        <div
          key={signal.label}
          className="flex items-start gap-3 rounded-xl border border-border/70 bg-background p-3 shadow-sm"
        >
          <div className={`rounded-full p-2 ${toneStyles[signal.tone]}`}>
            {signal.icon}
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-foreground">{signal.label}</p>
            <p className="text-xs text-muted-foreground">{signal.helper}</p>
            <Badge variant="outline">{signal.value}</Badge>
          </div>
        </div>
      ))}
    </div>
  );

  const handleUpgradeSubscription = useCallback(async (plan: SubscriptionPlan) => {
    const priceId = SUBSCRIPTION_PRICE_IDS[plan];
    if (!priceId) {
      toast.error("Subscription plan is not configured.");
      return;
    }

    try {
      const url = await createCheckoutSession({
        priceId,
        mode: "subscription",
        successUrl: `${window.location.origin}/profile?plan=${plan}&status=success`,
        cancelUrl: `${window.location.origin}/profile?plan=${plan}&status=cancelled`,
        metadata: {
          planType: plan,
          type: "subscription",
        },
      });

      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      toast.error("Unable to start verification checkout.");
      console.error(error);
    }
  }, [createCheckoutSession]);

  return (
    <>
      <Navbar />

      <div className="container max-w-6xl py-10 space-y-8">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white shadow-xl">
          <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.2), transparent 65%)" }} />
          <div className="relative z-10 flex flex-col gap-8 p-8 md:flex-row md:items-end md:justify-between">
            <div className="flex flex-col gap-6 md:flex-row md:items-center">
              <Avatar className="size-20 border-2 border-white/40 shadow-lg">
                {avatarImage ? (
                  <AvatarImage src={avatarImage} alt={displayName} />
                ) : (
                  <AvatarFallback className="bg-white/10 text-lg font-semibold">
                    {initials}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="space-y-3">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3 text-lg font-semibold md:text-2xl">
                    <Sparkles className="size-5 text-orange-300" />
                    <span>{displayName}</span>
                  </div>
                  <p className="text-sm text-white/70">{primaryEmail}</p>
                  {profile.bio && <p className="max-w-2xl text-sm text-white/80">{profile.bio}</p>}
                  <div className="flex flex-wrap items-center gap-2">
                    {heroBadges}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-xs text-white/60">
                  {memberSince && (
                    <span className="flex items-center gap-1">
                      <Clock className="size-3.5" /> Member since {memberSince.toLocaleDateString()}
                    </span>
                  )}
                  {lastSignedIn && (
                    <span>Last active {lastSignedIn.toLocaleString()}</span>
                  )}
                  <span>User ID {profile._id}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-start gap-3 md:items-end">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  Edit profile
                </Button>
                <Link href="/dashboard">
                  <a className={buttonVariants({ variant: "ghost", size: "sm" })}>
                    Manage listings
                  </a>
                </Link>
              </div>
              <div className="grid gap-3 rounded-2xl bg-white/10 p-4 text-white shadow-md backdrop-blur">
                {heroStats.map((stat) => (
                  <div key={stat.label} className="flex items-center justify-between gap-8 text-sm uppercase tracking-wide">
                    <div className="flex items-center gap-2 text-white/80">
                      {stat.icon}
                      {stat.label}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold">{stat.value}</p>
                      <p className="text-xs text-white/60">{stat.helper}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <section className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-xl">About you</CardTitle>
                  <CardDescription>Update your info, fine-tune first impressions, and keep buyers feeling confident.</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing((prev) => !prev)}
                >
                  {isEditing ? t("common.cancel") : t("profile.editProfile")}
                </Button>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <form onSubmit={handleUpdateProfile} className="grid gap-4">
                    <div className="grid gap-2">
                      <label className="text-sm font-medium">Name</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your name"
                      />
                    </div>
                    <div className="grid gap-2">
                      <label className="text-sm font-medium">Email</label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="you@example.com"
                      />
                    </div>
                    <div className="grid gap-2">
                      <label className="text-sm font-medium">Phone</label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                    <div className="grid gap-2">
                      <label className="text-sm font-medium">Bio</label>
                      <Textarea
                        name="bio"
                        value={formData.bio}
                        onChange={handleInputChange}
                        placeholder="Tell us about your marketplace style, trust signals, or specialties."
                        rows={4}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <Loader2 className="size-4 animate-spin" /> Saving
                          </>
                        ) : (
                          "Save changes"
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsEditing(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="grid gap-6">
                    <div className="grid gap-2">
                      <span className="text-sm text-muted-foreground">Primary email</span>
                      <span className="font-medium">{primaryEmail}</span>
                    </div>
                    <div className="grid gap-2">
                      <span className="text-sm text-muted-foreground">Phone</span>
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="size-4 text-muted-foreground" />
                        <span className="font-medium">{profile.phone || "Not set"}</span>
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <span className="text-sm text-muted-foreground">Short bio</span>
                      <span className="font-medium leading-relaxed">
                        {profile.bio || "Add a friendly intro to boost conversions."}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Active listings spotlight</CardTitle>
                <CardDescription>Top-performing ads and quick metrics to help you decide the next move.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {latestAds.length === 0 ? (
                  <div className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
                    No listings yet. Post an ad to unlock this dashboard.
                  </div>
                ) : (
                  latestAds.map((ad) => (
                    <div key={ad._id} className="rounded-xl border border-border/70 p-4 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="font-semibold">{ad.title}</p>
                          <p className="text-xs text-muted-foreground">Updated {new Date(ad.updatedAt ?? ad.createdAt).toLocaleString()}</p>
                        </div>
                        <Badge className="capitalize">
                          {ad.status || "draft"}
                        </Badge>
                      </div>
                      <Separator className="my-3" />
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span>{ad.price ? `$${numberFormatter.format(ad.price)}` : "Price on request"}</span>
                        <span>{numberFormatter.format(ad.views ?? 0)} impressions</span>
                        {ad.location && <span>{ad.location}</span>}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Latest responses</CardTitle>
                <CardDescription>See buyers leaning in and follow up faster.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {latestResponses.length === 0 ? (
                  <div className="rounded-lg border border-dashed py-8 text-center text-sm text-muted-foreground">
                    No replies yet. Promote a listing or invite previous buyers.
                  </div>
                ) : (
                  latestResponses.map((response) => (
                    <div key={response._id} className="rounded-xl border border-border/70 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-sm">
                          <MessageSquare className="size-4 text-primary" />
                          <span className="font-semibold">Conversation spark</span>
                        </div>
                        <Badge variant={response.status === "new" ? "default" : "secondary"}>
                          {response.status ?? "new"}
                        </Badge>
                      </div>
                      {response.message && (
                        <p className="mt-3 text-sm text-muted-foreground">{response.message}</p>
                      )}
                      <p className="mt-2 text-xs text-muted-foreground">
                        {new Date(response.createdAt).toLocaleString()}
                      </p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Profile completion</CardTitle>
                <CardDescription>Boost trust and ranking by finishing these steps.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{completionValue}% complete</span>
                  <span className="text-muted-foreground">{completedSteps} / {completionItems.length}</span>
                </div>
                <Progress value={completionValue} />
                <div className="space-y-3">
                  {completionItems.map((item) => (
                    <div key={item.label} className="flex items-center gap-3 text-sm">
                      {item.completed ? (
                        <CheckCircle2 className="size-4 text-emerald-500" />
                      ) : (
                        <AlertCircle className="size-4 text-amber-500" />
                      )}
                      <span className={item.completed ? "text-foreground" : "text-muted-foreground"}>
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Paywall
              tier="verified_trader"
              title="Verified trader trust center"
              subtitle="Earn the badge that unlocks early listing access, ad-free browsing, and priority support."
              perks={[
                { label: "Instant verification badge across your listings" },
                { label: "Buyer protection concierge for escalations" },
                { label: "Prominent placement in trust-first search" },
                { label: "Ability to showcase advanced safety credentials" },
              ]}
              className="border-primary/40 bg-primary/5"
              onCtaClick={() => handleUpgradeSubscription("verified_trader")}
              ctaLabel="Unlock Verified Trader"
            >
              {trustPreview}
            </Paywall>

            <PromotionSpotlight
              title="Level up your reputation"
              description="Stack automation and protection perks to stay ahead of every negotiation."
              audience={["buyer", "seller"]}
              className="rounded-3xl border border-border/60 bg-muted/30 p-6"
              excludeIds={["verified-trader"]}
            />

            <Card>
              <CardHeader>
                <CardTitle>Wallet verification</CardTitle>
                <CardDescription>Connect your crypto identity to unlock token-gated deals.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {profile.isWalletVerified ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <ShieldCheck className="size-4 text-emerald-500" />
                      <span className="font-semibold text-emerald-500">Wallet verified</span>
                    </div>
                    <div className="rounded-lg border bg-muted/40 p-3">
                      <p className="text-xs text-muted-foreground">Wallet address</p>
                      <p className="font-mono text-sm break-all">{profile.walletAddress}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      You can now join premium drops, reserve private listings, and access pay-to-win perks instantly.
                    </p>
                  </div>
                ) : (
                  <>
                    {!showWalletForm ? (
                      <Button onClick={() => setShowWalletForm(true)}>
                        <Lock className="size-4" />
                        Verify wallet
                      </Button>
                    ) : (
                      <form onSubmit={handleVerifyWallet} className="space-y-4">
                        <div className="grid gap-2">
                          <label className="text-sm font-medium">Wallet address</label>
                          <Input
                            value={walletData.walletAddress}
                            onChange={(e) =>
                              setWalletData({
                                ...walletData,
                                walletAddress: e.target.value,
                              })
                            }
                            placeholder="0x..."
                          />
                        </div>
                        <div className="grid gap-2">
                          <label className="text-sm font-medium">Signature (optional for demo)</label>
                          <Input
                            value={walletData.signature}
                            onChange={(e) =>
                              setWalletData({
                                ...walletData,
                                signature: e.target.value,
                              })
                            }
                            placeholder="Paste your signature"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? (
                              <>
                                <Loader2 className="size-4 animate-spin" /> Verifying
                              </>
                            ) : (
                              "Verify"
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowWalletForm(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Connect a wallet to prove authenticity, speed up payouts, and build trust with high-intent buyers.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Inbox pulses</CardTitle>
                <CardDescription>Latest notifications, so nothing slips through.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {latestNotifications.length === 0 ? (
                  <div className="rounded-lg border border-dashed py-8 text-center text-sm text-muted-foreground">
                    No notifications yet. Sit tight or start a campaign.
                  </div>
                ) : (
                  latestNotifications.map((notification) => (
                    <div key={notification._id} className="flex items-start gap-3 rounded-xl border border-border/70 p-3">
                      <div className="rounded-full bg-primary/10 p-2 text-primary">
                        <Bell className="size-4" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold">{notification.title}</p>
                        <p className="text-xs text-muted-foreground">{notification.message}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {new Date(notification.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </>
  );
}
